import { baseApi } from "./baseApi";
import * as API from "@utils/apiPath";
import type {
  ApiResponse,
  TableFilterParams,
  TripCancelManagerPayload,
} from "interface/tripCancelManager";
import type { StatusInterface } from "interface/common";
import { PAGE_LIMIT } from "@utils/constant/common";

const getFormattedParams = (params: TableFilterParams = {}) => {
  const normalizeFilterValue = (value?: string | string[] | null): string => {
    if (Array.isArray(value)) return value[0] ?? "";
    return value ?? "";
  };

  const normalizeMultiValue = (value?: string[] | string | null): string => {
    if (value == null) return "";
    if (Array.isArray(value)) return value.length ? value.join("-") : "";
    if (typeof value === "string") return value.trim();
    return "";
  };

  const p = params as TableFilterParams & {
    sort?: string;
    direction?: "asc" | "desc";
    limit?: number;
  };

  const page = p.page ?? 1;
  const limit = p.pageSize ?? p.limit ?? PAGE_LIMIT;

  const formatted: Record<string, string | number> = {
    page,
    limit,
  };

  const title = normalizeFilterValue(p.title);
  const type = normalizeMultiValue(p.type);
  const status = normalizeMultiValue(p.status);
  const updatedAt = normalizeFilterValue(p.updatedAt);

  if (title) formatted.title = title;
  if (type) formatted.type = type;
  if (status) formatted.status = status;
  if (updatedAt) formatted.updatedAt = updatedAt;

  const sortField = p.sortField ?? p.sort;
  if (sortField) {
    formatted.sort = sortField;
    if (p.sortOrder === "ascend") formatted.direction = "asc";
    else if (p.sortOrder === "descend") formatted.direction = "desc";
    else if (p.direction === "asc" || p.direction === "desc") {
      formatted.direction = p.direction;
    }
  }

  return formatted;
};

export const tripCancelManagerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTripCancelManagers: builder.query<ApiResponse, TableFilterParams>({
      query: (params) => ({
        url: API.GET_TRIP_CANCEL_MANAGER,
        method: "GET",
        params: getFormattedParams(params),
      }),
      providesTags: ["TripCancelManager"],
    }),
    createTripCancelManager: builder.mutation<ApiResponse, TripCancelManagerPayload>({
      query: ({ params }) => ({
        url: API.GET_TRIP_CANCEL_MANAGER,
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["TripCancelManager"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to create trip cancel option:", error);
        }
      },
    }),
    updateTripCancelManager: builder.mutation<ApiResponse, TripCancelManagerPayload>({
      query: ({ params }) => ({
        url: API.GET_TRIP_CANCEL_MANAGER,
        method: "PUT",
        body: params,
      }),
      invalidatesTags: ["TripCancelManager"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
          arg.onClose();
        } catch (error) {
          console.error("Failed to update trip cancel option:", error);
        }
      },
    }),
    updateTripCancelManagerStatus: builder.mutation<ApiResponse, StatusInterface>({
      query: ({ id, ...body }) => ({
        url: `/${API.GET_TRIP_CANCEL_MANAGER}/status/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["TripCancelManager"],
    }),
    deleteTripCancelManager: builder.mutation<ApiResponse, string>({
      query: (id) => ({
        url: `/${API.GET_TRIP_CANCEL_MANAGER}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TripCancelManager"],
    }),
    bulkUpdateTripCancelManagerStatus: builder.mutation<
      ApiResponse,
      { ids: string[]; status: boolean }
    >({
      query: ({ ids, status }) => ({
        url: `/${API.GET_TRIP_CANCEL_MANAGER}/bulk-status`,
        method: "PUT",
        body: { ids, status },
      }),
      invalidatesTags: ["TripCancelManager"],
    }),
    bulkDeleteTripCancelManagers: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_TRIP_CANCEL_MANAGER}/bulk-delete`,
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["TripCancelManager"],
    }),
    reorderTripCancelManagers: builder.mutation<ApiResponse, { ids: string[] }>({
      query: ({ ids }) => ({
        url: `/${API.GET_TRIP_CANCEL_MANAGER}/reorder`,
        method: "PUT",
        body: { ids },
      }),
      invalidatesTags: ["TripCancelManager"],
    }),
  }),
});

export const {
  useGetTripCancelManagersQuery,
  useCreateTripCancelManagerMutation,
  useUpdateTripCancelManagerMutation,
  useUpdateTripCancelManagerStatusMutation,
  useDeleteTripCancelManagerMutation,
  useBulkUpdateTripCancelManagerStatusMutation,
  useBulkDeleteTripCancelManagersMutation,
  useReorderTripCancelManagersMutation,
} = tripCancelManagerApi;
